import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, AtSign } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "username" ? "username" : "password");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "username" ? "/auth/forgot-username" : "/auth/forgot-password";
      const { data } = await api.post(path, { email });
      setSent(true);
      toast.success(data.message || "Request received");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-10">
      <div className="w-full max-w-md fade-up" data-testid="forgot-page">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <p className="font-display text-lg font-extrabold tracking-tight text-slate-900">ARPS</p>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-2">
          <button data-testid="forgot-tab-password" type="button" onClick={() => { setMode("password"); setSent(false); }}
            className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-semibold transition ${mode === "password" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            <KeyRound className="h-4 w-4" /> Forgot password
          </button>
          <button data-testid="forgot-tab-username" type="button" onClick={() => { setMode("username"); setSent(false); }}
            className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-semibold transition ${mode === "username" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            <AtSign className="h-4 w-4" /> Forgot username
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            {mode === "username" ? "Recover your username" : "Reset your password"}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {mode === "username"
              ? "Enter your registered email and we'll send your account information to that address."
              : "Enter your registered email and we'll send a secure, single-use reset link."}
          </p>

          {sent ? (
            <div data-testid="forgot-sent-message" className="mt-5 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800">
              If an account exists for <strong>{email}</strong>, {mode === "username" ? "your account information has" : "a secure reset link has"} been sent. Please check your inbox.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input data-testid="forgot-email-input" id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lawfirm.com" />
              </div>
              <Button data-testid="forgot-submit-button" type="submit" disabled={busy} className="w-full bg-slate-900 text-white hover:bg-slate-800">
                {busy ? "Sending…" : mode === "username" ? "Send account info" : "Send reset link"}
              </Button>
            </form>
          )}

          <button data-testid="back-to-login" onClick={() => nav("/login")} className="mt-5 w-full text-center text-sm font-semibold text-emerald-600 hover:underline">
            ← Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
